import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import axiosInstance from '@API/axios';
import { setSnackbar } from '@Redux/snackBarSlice';
import { EXIT_PLAN_BY_ID, EXIT_PLAN_APPROVE, EXEMPTION_LOCAL_RISK_ASSESSMENT } from '@API/APIRoutes';

const errorText = (err, fallback) =>
  (typeof err?.response?.data === 'string' && err.response.data) || err?.response?.data?.message || fallback;

/** Loads one exemption request (ExitPlanResponseDto) and exposes the two workflow actions. */
export const useExemptionRequest = (planId) => {
  const dispatch = useDispatch();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(EXIT_PLAN_BY_ID(planId));
      setPlan(data);
    } catch (err) {
      dispatch(setSnackbar({ open: true, message: errorText(err, 'Failed to load the exemption request.'), error: true }));
    } finally {
      setLoading(false);
    }
  }, [planId, dispatch]);

  useEffect(() => {
    reload();
  }, [reload]);

  const submitLocalRiskAssessment = async (localRiskAssessment) => {
    setActing(true);
    try {
      const { data } = await axiosInstance.post(EXEMPTION_LOCAL_RISK_ASSESSMENT(planId), { localRiskAssessment });
      setPlan(data);
      dispatch(setSnackbar({ open: true, message: 'Local risk assessment submitted. The request was forwarded for approval.', error: false }));
      return true;
    } catch (err) {
      dispatch(setSnackbar({ open: true, message: errorText(err, 'Failed to submit the local risk assessment.'), error: true }));
      return false;
    } finally {
      setActing(false);
    }
  };

  const decide = async (approved, comment) => {
    setActing(true);
    try {
      const { data } = await axiosInstance.post(EXIT_PLAN_APPROVE(planId), { approved, comment });
      setPlan(data);
      dispatch(setSnackbar({ open: true, message: approved ? 'Exemption request approved.' : 'Exemption request rejected.', error: false }));
      return true;
    } catch (err) {
      dispatch(setSnackbar({ open: true, message: errorText(err, 'Failed to process the decision.'), error: true }));
      return false;
    } finally {
      setActing(false);
    }
  };

  return { plan, loading, acting, reload, submitLocalRiskAssessment, decide };
};

export default useExemptionRequest;
